import { gatherAnalyticsData } from '@/util/helpers'
import Device from '@/util/Device'
import { extractErrorMessage, normalizeToError } from '@/services/errors/errorUtils'

/**
 * CENTRALIZED ERROR SERVICE
 * =========================
 * Single source of truth for all error handling and reporting.
 * 
 * Features:
 * - Catches all types of errors (React, global JS, promise rejections)
 * - Sends to configurable reporters (Mixpanel, future services)
 * - Enriches errors with context data
 * - Rate limiting to prevent spam
 * - Development logging
 * - Test utilities
 * 
 * Usage:
 * ```
 * import ErrorService from '@/services/errors/ErrorService'
 * 
 * // Basic usage
 * ErrorService.captureError(error)
 * 
 * // With component context
 * ErrorService.captureError(error, {
 *   component: 'PlaceBet',
 *   context: { marketId: 123, outcomeId: 456 }
 * })
 * ```
 */

class ErrorService {
  constructor() {
    this.reporters = []
    this.enrichers = []
    this.isInitialized = false
    this.isDevelopment = import.meta.env.MODE === 'development'
    
    // Rate limiting configuration
    this.rateLimiting = {
      enabled: true,
      maxErrorsPerMinute: 10,
      maxErrorsPerSession: 100,
    }
    
    // Error tracking for rate limiting
    this.errorCount = {
      lastMinute: [],
      session: 0,
    }
    
    // Error deduplication
    this.recentErrors = new Map() // key: error fingerprint, value: timestamp
    this.deduplicationWindow = 5000 // 5 seconds
    
    // ⭐ Component context registry
    // Stores context data registered by components via useErrorContext hook
    this.componentContexts = new Map() // key: componentId, value: { componentName, context, timestamp }
    this.contextIdCounter = 0
  }

  /**
   * Initialize the error service with reporters and configuration
   */
  configure(config = {}) {
    if (this.isInitialized) {
      console.warn('[ErrorService] Already initialized')
      return
    }

    const {
      reporters = [],
      enrichers = [],
      rateLimiting = {},
    } = config

    this.reporters = reporters
    this.enrichers = enrichers
    
    // Merge rate limiting config
    this.rateLimiting = {
      ...this.rateLimiting,
      ...rateLimiting,
    }

    // Install global error handlers
    this.installGlobalHandlers()

    this.isInitialized = true
  }

  /**
   * Install global error handlers for non-React errors
   */
  installGlobalHandlers() {
    if (typeof window === 'undefined') return
    // Catch global JavaScript errors
    window.addEventListener('error', (event) => {
      const filename = event.filename || ''
      const shortFilename = filename.split('/').pop()?.replace(/\.\w+$/, '') || 'global'
      this.captureError(event.error || new Error(event.message), {
        component: shortFilename,
        type: 'global_error',
        filename,
        lineno: event.lineno,
        colno: event.colno,
      })
    })

    // Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      const reason = event.reason

      // Ignore transport-level request failures (fetch rejected with no HTTP
      // response: offline, DNS, CORS, timeout, or regional blocking). These are
      // connectivity issues rather than app errors, and would otherwise flood
      // error monitoring — especially from regions where our API or third-party
      // domains are blocked. Real backend errors (with an HTTP status/body) and
      // all other rejections still report normally.
      if (this.isIgnorableNetworkRejection(reason)) return

      // Ignore expected 404s on deleted/missing category endpoints. Scrapers and
      // stale category URLs hit these often; the API correctly returns 404 and the
      // store already records the failure — reporting them as Mixpanel Error noise
      // drowns out real product bugs.
      if (this.isIgnorableCategoryNotFound(reason)) return

      const error = normalizeToError(reason ?? 'Unknown rejection')

      // Surface request details attached by the HTTP layer (request.js) so failed
      // API calls report which endpoint failed and the underlying reason.
      const context = {}
      if (reason && typeof reason === 'object') {
        if (reason.url) context.request_url = reason.url
        if (reason.method) context.request_method = reason.method
        if (reason.status != null) context.request_status = reason.status
        if (reason.reason) context.failure_reason = reason.reason
        if (reason.reason_name) context.failure_reason_name = reason.reason_name
      }

      this.captureError(error, {
        component: this.resolveUnhandledComponent(error, reason),
        type: 'unhandled_promise_rejection',
        context,
      })
    })
  }

  /**
   * Detect a transport-level request failure thrown by the HTTP layer
   * (request.js) when fetch() has no HTTP response. These represent
   * connectivity/network problems (offline, DNS, CORS, timeout, regional
   * blocking), not application errors, so they are not worth reporting.
   *
   * @param {unknown} reason - The rejection reason
   * @returns {boolean}
   */
  isIgnorableNetworkRejection(reason) {
    if (!reason || typeof reason !== 'object') return false
    const message = reason?.error?.message ?? reason?.message
    return reason.status === 500 && message === 'Server is not available'
  }

  /**
   * Expected missing-category responses (deleted categories / scraper hits on
   * stale `/markets/.../{id}` URLs). Django returns detail "Not found." (EN) or
   * "Não encontrado." (PT).
   *
   * @param {unknown} reason
   * @returns {boolean}
   */
  isIgnorableCategoryNotFound(reason) {
    if (!reason || typeof reason !== 'object') return false
    if (reason.status !== 404) return false

    const url = typeof reason.url === 'string' ? reason.url : ''
    if (!/\/categories\/\d+\/?/.test(url)) return false

    const message = extractErrorMessage(reason)
    return (
      message === 'Not found.' ||
      message === 'Não encontrado.' ||
      /^not found\.?$/i.test(message)
    )
  }

  /**
   * Production Vite builds mangle locals to 1–2 letter names (`O`, `D`). Those are
   * not useful as Mixpanel "component" labels.
   *
   * @param {string} name
   * @returns {boolean}
   */
  isUsefulStackName(name) {
    if (!name || typeof name !== 'string') return false
    if (name.length <= 2) return false
    // Common noise from stacks / React internals
    if (/^(Object|Array|Promise|Function|Anonymous|anonymous)$/.test(name)) return false
    return true
  }

  /**
   * Map API path segments to stable Mixpanel labels (readable, not mangled).
   *
   * @param {string} pathname
   * @returns {string|null}
   */
  labelForApiPath(pathname) {
    const segments = pathname.split('/').filter(Boolean)
    // Skip version segments like "v1.5"
    const meaningful = segments.filter(
      segment => !/^v\d/i.test(segment) && !/^\d+$/.test(segment)
    )
    if (meaningful.length === 0) return null

    const [resource, action] = meaningful
    const labels = {
      categories: action ? `Category.${action}` : 'CategoryFetch',
      markets: action ? `Market.${action}` : 'MarketFetch',
      questions: action ? `Question.${action}` : 'QuestionFetch',
      orders: action ? `Order.${action}` : 'OrderFetch',
      bets: action ? `Bet.${action}` : 'BetFetch',
      account: action ? `Account.${action}` : 'AccountFetch',
      users: action ? `User.${action}` : 'UserFetch',
      wallets: action ? `Wallet.${action}` : 'WalletFetch',
      finance: action ? `Finance.${action}` : 'FinanceFetch',
      auth: action ? `Auth.${action}` : 'AuthFetch',
      comments: action ? `Comment.${action}` : 'CommentFetch',
    }

    if (labels[resource]) return labels[resource]
    return action ? `api:${resource}.${action}` : `api:${resource}`
  }

  /**
   * Prefer a stable label over production-minified stack frames (`O`, `D`, …).
   * Those single-letter names come from Vite's mangled bundle, not React components.
   *
   * @param {Error} error
   * @param {unknown} reason
   * @returns {string}
   */
  resolveUnhandledComponent(error, reason) {
    const url = reason && typeof reason === 'object' ? reason.url : null
    if (typeof url === 'string' && url.length > 0) {
      try {
        const path = new URL(url, 'https://api.futuur.com').pathname
        const label = this.labelForApiPath(path)
        if (label) return label
      } catch {
        // Fall through to stack / default
      }
    }

    if (error?.stack) {
      const namedFrames = [...error.stack.matchAll(/at\s+([A-Za-z_$][\w$]*)/g)]
        .map(match => match[1])
        .filter(name => this.isUsefulStackName(name))

      if (namedFrames.length > 0) return namedFrames[0]

      const assetMatch = error.stack.match(/\/assets\/([A-Za-z][\w-]*)-[A-Za-z0-9_]+\.js/)
      if (assetMatch && this.isUsefulStackName(assetMatch[1])) return assetMatch[1]
    }

    return 'unhandled_promise'
  }

  /**
   * Main error capture method
   * 
   * @param {Error} error - The error object
   * @param {Object} metadata - Additional context data
   * @param {string} metadata.component - Component name where error occurred
   * @param {Object} metadata.context - Custom context data from component
   * @param {string} metadata.currentUrl - Current URL
   * @param {string} metadata.type - Error type (react, global_error, etc.)
   */
  async captureError(error, metadata = {}) {
    try {
      // Skip if not a real error
      if (!error) {
        return
      }

      error = normalizeToError(error)

      // Check rate limits
      if (!this.checkRateLimit()) {
        return
      }

      // Check for duplicates
      if (this.isDuplicate(error, metadata)) {
        return
      }

      // Create error fingerprint for tracking
      this.trackError(error, metadata)

      // Build error data
      const errorData = await this.buildErrorData(error, metadata)

      // Send to all reporters (blocking)
      this.reporters.forEach(reporter => {
        try {
          reporter.report(errorData)
        } catch (reporterError) {
          if (this.isDevelopment) {
            console.error('[ErrorService] Reporter failed:', reporterError)
          }
        }
      })

    } catch (captureError) {
      // Fail silently but log in development
      if (this.isDevelopment) {
        console.error('[ErrorService] Critical failure:', captureError)
      }
    }
  }

  /**
   * Build enriched error data
   */
  async buildErrorData(error, metadata) {
    // ⭐ Merge registered component context (from useErrorContext hook)
    const registeredContexts = this.getActiveComponentContexts()
    const mergedContext = {
      ...(metadata.context || {}),
      ...registeredContexts,
    }
    
    // Extract _componentName for internal use (don't include in final data)
    const { _componentName, ...contextWithoutInternals } = mergedContext
    
    const baseData = {
      // Error details
      error_message: extractErrorMessage(error),
      error_stack: error.stack || '',
      error_name: error.name || 'Error',
      
      // ⭐ Component name: use metadata.component, fallback to _componentName, fallback to 'unknown'
      component: metadata.component || _componentName || 'unknown',
      error_type: metadata.type || 'react_error',
      
      // ⭐ Merged context (without internal fields like _componentName)
      ...contextWithoutInternals,
      
      // Device info not tracked by Mixpanel
      display_mode: Device.displayMode,
      is_mobile: Device.isMobile,
      platform: Device.isIOS ? 'ios' : Device.isAndroid ? 'android' : 'web',
    }

    try {
      const analyticsData = await gatherAnalyticsData()
      Object.assign(baseData, analyticsData)
    } catch (error) {
      // Silently fail analytics gathering
    }

    // Apply enrichers
    let enrichedData = { ...baseData }
    for (const enricher of this.enrichers) {
      try {
        enrichedData = await enricher(enrichedData, error, metadata)
      } catch (enricherError) {
        // Silently fail enrichers
      }
    }

    // Flatten any nested objects/arrays-of-objects to prevent [object Object] in analytics
    for (const [key, value] of Object.entries(enrichedData)) {
      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        enrichedData[key] = JSON.stringify(value)
      } else if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
        enrichedData[key] = JSON.stringify(value)
      }
    }

    return enrichedData
  }

  /**
   * Check if error should be reported based on rate limits
   */
  checkRateLimit() {
    if (!this.rateLimiting.enabled) {
      return true
    }

    const now = Date.now()
    const oneMinuteAgo = now - 60000

    // Clean old entries from last minute tracking
    this.errorCount.lastMinute = this.errorCount.lastMinute.filter(
      timestamp => timestamp > oneMinuteAgo
    )

    // Check per-minute limit
    if (this.errorCount.lastMinute.length >= this.rateLimiting.maxErrorsPerMinute) {
      return false
    }

    // Check session limit
    if (this.errorCount.session >= this.rateLimiting.maxErrorsPerSession) {
      return false
    }

    return true
  }

  /**
   * Track error for rate limiting and deduplication
   */
  trackError(error, metadata) {
    const now = Date.now()
    
    // Update rate limiting counters
    this.errorCount.lastMinute.push(now)
    this.errorCount.session++

    // Update deduplication map
    const fingerprint = this.getErrorFingerprint(error, metadata)
    this.recentErrors.set(fingerprint, now)

    // Clean old entries from deduplication map
    for (const [key, timestamp] of this.recentErrors.entries()) {
      if (now - timestamp > this.deduplicationWindow) {
        this.recentErrors.delete(key)
      }
    }
  }

  /**
   * Check if error is a duplicate
   */
  isDuplicate(error, metadata) {
    const fingerprint = this.getErrorFingerprint(error, metadata)
    const lastSeen = this.recentErrors.get(fingerprint)
    
    if (!lastSeen) {
      return false
    }

    const timeSinceLastSeen = Date.now() - lastSeen
    return timeSinceLastSeen < this.deduplicationWindow
  }

  /**
   * Generate error fingerprint for deduplication
   */
  getErrorFingerprint(error, metadata) {
    const message = extractErrorMessage(error)
    const component = metadata.component || 'unknown'
    const type = metadata.type || 'unknown'
    
    // Simple fingerprint: type + component + first line of message
    const firstLine = message.split('\n')[0]
    return `${type}:${component}:${firstLine}`
  }

  /**
   * Update rate limiting configuration at runtime
   */
  updateRateLimiting(config) {
    this.rateLimiting = {
      ...this.rateLimiting,
      ...config,
    }
  }

  /**
   * Test utility: Manually trigger an error for testing
   */
  testError(message = 'Test error', metadata = {}) {
    const testError = new Error(message)
    this.captureError(testError, {
      ...metadata,
      type: 'test_error',
      component: metadata.component || 'ErrorService.testError',
    })
  }

  /**
   * Get current error statistics
   */
  getStats() {
    return {
      errorsLastMinute: this.errorCount.lastMinute.length,
      errorsThisSession: this.errorCount.session,
      rateLimiting: this.rateLimiting,
      recentErrorsCount: this.recentErrors.size,
    }
  }

  /**
   * Reset error counters (useful for testing)
   */
  resetCounters() {
    this.errorCount.lastMinute = []
    this.errorCount.session = 0
    this.recentErrors.clear()
  }

  /**
   * ⭐ Register component context (called by useErrorContext hook)
   * Returns a unique ID for unregistering later
   */
  registerComponentContext(componentName, context) {
    const contextId = `${componentName}_${++this.contextIdCounter}_${Date.now()}`
    
    this.componentContexts.set(contextId, {
      componentName,
      context,
      timestamp: Date.now(),
    })

    return contextId
  }

  /**
   * ⭐ Unregister component context (called when component unmounts)
   */
  unregisterComponentContext(contextId) {
    this.componentContexts.delete(contextId)
  }

  /**
   * ⭐ Get all active component contexts merged into one object
   * More recent contexts override older ones with same keys
   */
  getActiveComponentContexts() {
    const mergedContext = {}
    const contexts = Array.from(this.componentContexts.values())
    
    // Sort by timestamp (oldest first, so newer ones override)
    contexts.sort((a, b) => a.timestamp - b.timestamp)
    
    // Merge all contexts
    for (const { context } of contexts) {
      Object.assign(mergedContext, context)
    }

    return mergedContext
  }

  /**
   * Get information about registered contexts (for debugging)
   */
  getRegisteredContexts() {
    return Array.from(this.componentContexts.entries()).map(([id, data]) => ({
      id,
      componentName: data.componentName,
      context: data.context,
      age: Date.now() - data.timestamp,
    }))
  }
}

// Create singleton instance
const errorService = new ErrorService()

// Export singleton
export default errorService

// Also export class for testing
export { ErrorService }

